import { AccountType } from "../../generated/core/v1/accounts";
import { ActionCategory, TransferFhsaToRrspAction_Reason } from "../../generated/core/v1/actions";
import { formatDate, MONTHS_SHORT, type PDate } from "../lib/dates";
import { money } from "../lib/format";
import { type EntityIndex, type FlatAction, isUnconfirmedFhsaTransfer } from ".";

/**
 * Words for the plan's actions ("Put $1,840 against your Mastercard"). Presentation only: the
 * action, its amount and its target all come from the plan; names come from the family.
 */
const NEW_ACCOUNT: Partial<Record<AccountType, string>> = {
  [AccountType.TFSA_ACCOUNT]: "TFSA",
  [AccountType.RRSP_ACCOUNT]: "RRSP",
  [AccountType.FHSA_ACCOUNT]: "FHSA",
  [AccountType.RESP_ACCOUNT]: "RESP",
  [AccountType.NON_REGISTERED_INVESTMENT_ACCOUNT]: "non-registered account",
  [AccountType.CHEQUING_ACCOUNT]: "savings account",
  [AccountType.RRIF_ACCOUNT]: "RRIF",
};

function your(index: EntityIndex, uuid: string, fallback: string): string {
  const info = index.get(uuid);
  if (!info) return `your ${fallback}`;
  // Names like "Alex’s TFSA" read better without "your".
  return /’s |'s /.test(info.name) ? info.name : `your ${info.name}`;
}

export function actionTitle(f: FlatAction, index: EntityIndex, amount = f.amount): string {
  const m = money(amount);
  const t = f.action.actionType;
  switch (f.kind) {
    case "moveMoneyFromChequingToDebtAction":
      return `Put ${m} against ${your(index, f.targetUuid, "debt")}`;
    case "moveMoneyFromChequingToAccountAction":
      return `Put ${m} into ${your(index, f.targetUuid, "account")}`;
    case "moveMoneyFromChequingToExpenseAction":
      return `Pay ${m} for ${index.name(f.targetUuid, "a bill")}`;
    case "moveMoneyIntoChequingAction":
      return `Move ${m} from ${your(index, f.targetUuid, "account")} into chequing`;
    case "payPriorityAction":
      return `Put ${m} toward ${index.name(f.targetUuid, "a priority")}`;
    case "transferFhsaToRrspAction": {
      // Named like the household's other accounts ("Robin’s RRSP"), as the reason under it is.
      const who = index.get(f.memberUuid)?.name;
      return who && who !== "You"
        ? `Move ${m} from ${who}’s FHSA into their RRSP`
        : `Move ${m} from your FHSA into your RRSP`;
    }
    case "convertRrspToRrifAction":
      return `Convert ${your(index, f.targetUuid, "RRSP")} to a RRIF`;
    case "createHouseAndMortgageAction":
      return `Buy ${index.name(f.targetUuid, "your home")}, with ${m} down`;
    case "reamortizeDebtsIntoMortgageAction":
      return `Fold ${m} of debt into ${your(index, f.targetUuid, "mortgage")}`;
    case "setGroupRrspContributionAction": {
      if (t.oneofKind !== "setGroupRrspContributionAction") return "";
      const { employeeContributionRate: rate, previousEmployeeContributionRate: was } =
        t.setGroupRrspContributionAction;
      const pct = (r: number) => `${+(r * 100).toFixed(2)}%`;
      if (!was) return `Set your group RRSP contribution to ${pct(rate)} of your pay`;
      return `${rate > was ? "Raise" : "Lower"} your group RRSP contribution from ${pct(was)} to ${pct(rate)} of your pay`;
    }
    case "createdAccountMove": {
      const type =
        t.oneofKind === "createAccountAction"
          ? (NEW_ACCOUNT[t.createAccountAction.accountType] ?? "account")
          : "account";
      return f.direction === "in"
        ? `Move ${m} from your new ${type} into chequing`
        : `Put ${m} into a new ${type}`;
    }
    default:
      return `Move ${m}`;
  }
}

/**
 * The warning on an FHSA move the household hasn't confirmed: the data says the holder isn't a
 * first-time home buyer, which may be a mistake (`TransferFhsaToRrspAction` reason
 * NOT_FIRST_TIME_BUYER_UNCONFIRMED).
 */
export function fhsaWarning(f: FlatAction, index: EntityIndex): string {
  const who = index.get(f.memberUuid)?.name;
  if (!who || who === "You")
    return "Your data says you aren’t a first-time home buyer. If that’s correct, move your FHSA balance to your RRSP. If it’s a mistake, update your data before you do this.";
  return `Your data says ${who} isn’t a first-time home buyer. If that’s correct, move ${who}’s FHSA balance to their RRSP. If it’s a mistake, update your data before you do this.`;
}

/**
 * Why the plan moves an FHSA to the RRSP, from the action's `reason` (empty for an unconfirmed
 * move, which gets `fhsaWarning`, and for a move without a reason). The move is tax-free and
 * doesn't use RRSP room; the FHSA closes.
 */
export function fhsaMoveReason(f: FlatAction, index: EntityIndex): string {
  const t = f.action.actionType;
  if (t.oneofKind !== "transferFhsaToRrspAction") return "";
  const who = index.get(f.memberUuid)?.name;
  const you = !who || who === "You";
  const fhsa = you ? "your FHSA" : `${who}’s FHSA`;
  const rrsp = you ? "your RRSP" : "their RRSP";
  switch (t.transferFhsaToRrspAction.reason) {
    case TransferFhsaToRrspAction_Reason.NOT_FIRST_TIME_BUYER_CONFIRMED:
      return "You confirmed it: not a first-time home buyer, so it moves to the RRSP, tax-free.";
    case TransferFhsaToRrspAction_Reason.NO_HOMEBUYING_GOAL:
      return `You have no home-buying goal, so Abi moves ${fhsa} to ${rrsp}, where it keeps growing tax-sheltered. Add “Buying a home” to your priorities if you’re planning to buy.`;
    case TransferFhsaToRrspAction_Reason.HOMEBUYING_GOAL_DUE:
      return `Your home-buying goal is due, so what’s left in ${fhsa} moves to ${rrsp}, tax-free, where it keeps growing tax-sheltered.`;
    case TransferFhsaToRrspAction_Reason.HOME_PURCHASED:
      return `${you ? "Your FHSA" : fhsa} has gone toward your home, so what’s left moves to ${rrsp}, tax-free, before the FHSA closes.`;
    case TransferFhsaToRrspAction_Reason.AGE_71:
      return `An FHSA must close by the end of the year its holder turns 71, so Abi moves ${fhsa} to ${rrsp}, tax-free.`;
    default:
      return "";
  }
}

/** Why, in one line, from the action's category (no per-action impact: the engine has none). */
export function actionWhy(f: FlatAction, index: EntityIndex): string {
  if (isUnconfirmedFhsaTransfer(f.action))
    return "Warning: check the warning at the top of this list before you do this.";
  const fhsa = fhsaMoveReason(f, index);
  if (fhsa) return fhsa;
  if (f.action.actionType.oneofKind === "setGroupRrspContributionAction") {
    const { employeeContributionRate: rate, previousEmployeeContributionRate: was } =
      f.action.actionType.setGroupRrspContributionAction;
    return rate < was
      ? "Abi keeps it to what your employer matches and your cash flow can carry, and checks again every year."
      : "Your employer matches it. Abi picks the most your cash flow can carry, up to the cap, and checks again every year.";
  }
  const info = index.get(f.targetUuid);
  switch (f.category) {
    case ActionCategory.DEBT_EXTRA:
      return info?.rate !== undefined
        ? `Above the minimum. At ${+info.rate.toFixed(2)}%, this is where Abi sends spare money first.`
        : "Above the minimum, to clear it sooner.";
    case ActionCategory.DEBT_MINIMUM:
      return "The minimum payment.";
    case ActionCategory.SAVING:
      return "Saving and investing, in the account that keeps the most of it away from tax.";
    case ActionCategory.PRIORITY:
      return "Toward one of your priorities.";
    case ActionCategory.WITHDRAWAL:
      return "Drawing on savings to cover what's due.";
    default:
      return "";
  }
}

/** "By 15 Oct", or "This month" style due label. */
export function dueLabel(d: PDate, today: PDate): string {
  if (d.year === today.year && d.month === today.month) {
    return d.day ? `By ${d.day} ${MONTHS_SHORT[d.month - 1]}` : "This month";
  }
  return formatDate(d);
}
